import React, {useEffect} from 'react';
import {Box, Button, Container, Typography} from "@mui/material";
import {useForm} from "react-hook-form";
import {passwordSetRequestSchema, PasswordSetSchema} from "../../schemas/auth/passwordSetSchema";
import {zodResolver} from "@hookform/resolvers/zod";
import PasswordTextField from "../../components/input/PasswordTextField";
import {useNotification} from "../../utils/hooks/useNotification";
import {useNavigate, useSearchParams} from "react-router-dom";
import {postFetcher} from "../../utils/api/fetchers";

interface SetPasswordProps {
    reset?: boolean;
}

const SetPassword: React.FC<SetPasswordProps> = ({reset = false}) => {
    const {showNotification} = useNotification();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {register, handleSubmit, formState: {errors}, setValue} = useForm<PasswordSetSchema>({
        resolver: zodResolver(passwordSetRequestSchema)
    });

    const onSubmit = (data: PasswordSetSchema) => {
        postFetcher('/auth/reset-password', {
            newPassword: data.newPassword,
            token: data.token
        }).then(() => {
            showNotification('Heslo bylo úspěšně nastaveno', 'success');
            navigate('/login');
        }).catch((error) => {
            console.error(error);
            if (error.response?.status === 400) {
                showNotification('Platnost tokenu pro nastavení hesla vypršela', 'error');
            } else {
                showNotification('Nastala chyba při nastavování hesla', 'error');
            }
            navigate('/reset-password-request');
        });
    }

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            showNotification('Chybí token pro obnovu hesla', 'error');
            navigate('/login');
        } else {
            setValue('token', token);
        }
    });

    return (
        <Container component="main" maxWidth="sm" sx={{padding: {xs: 2, sm: 4}}}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mt: 8,
                    boxShadow: 5,
                    padding: {xs: 2, sm: 6},
                    borderRadius: 2,
                    backgroundColor: 'white',
                }}
            >
                <Typography component="h1" variant="h5">
                    {reset ? 'Obnova hesla' : 'Nastavení hesla'}
                </Typography>
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}
                    sx={{mt: 3, width: '100%'}}
                >
                    <PasswordTextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        label="Nové heslo"
                        type="password"
                        autoComplete="password"
                        autoFocus
                        {...register('newPassword')}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                    />
                    <PasswordTextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        label="Potvrzení nového hesla"
                        type="password"
                        autoComplete="password"
                        {...register('newPasswordConfirmation')}
                        error={!!errors.newPasswordConfirmation}
                        helperText={errors.newPasswordConfirmation?.message}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{mt: 3, mb: 2, fontWeight: 'bold'}}
                    >
                        Potvrdit
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default SetPassword;
